<template>
  <div data-test="graphics-socialDisplaySettings">
    <SidebarSettingsGroup
      title="Social Graphic Settings"
      collapsible
      :element-type="element.type"
      data-test="graphics-socialDisplaySettings-general"
    >
      <div class="settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label
            for="displaySelected"
            class="sidebar-form-label"
          >Social Graphic</label>
        </div>
        <div class="settings-col-12">
          <div class="theme-dropdown-wrapper">
            <div
              class="theme-dropdown-trigger"
              tabindex="0"
              @click="toggleThemeDropdown"
              @keydown.enter="toggleThemeDropdown"
              @keydown.space.prevent="toggleThemeDropdown"
            >
              <span class="theme-dropdown-value">
                {{ selectedDisplayTitle || 'Select a Social Graphic' }}
              </span>
              <span class="theme-dropdown-arrow">▼</span>
            </div>
            <div
              v-if="showThemeDropdown"
              class="theme-dropdown-menu"
            >
              <div class="theme-search-wrapper">
                <input
                  ref="themeSearchInput"
                  v-model="themeSearchQuery"
                  type="text"
                  class="theme-search-input"
                  placeholder="Search Social Graphics..."
                  @input="onThemeSearchInput"
                >
              </div>
              <div
                ref="themeDropdownList"
                class="theme-dropdown-list"
                @scroll="onThemeScroll"
              >
                <div
                  v-for="display in filteredDisplays"
                  :key="display._id"
                  class="theme-dropdown-item"
                  :class="{ 'active': display._id === displayId }"
                  :data-test="`graphics-selectSocialDisplay-${display._id}`"
                  @click="selectDisplay(display)"
                >
                  <div class="theme-dropdown-item__title">
                    {{ display.title }}
                  </div>
                  <div class="theme-dropdown-item__subtitle">
                    {{ getLayoutLabel(display.layout) }}
                  </div>
                </div>
                <div
                  v-if="loadingThemes"
                  class="theme-dropdown-loading"
                >
                  Loading Social Graphics...
                </div>
                <div
                  v-if="!loadingThemes && filteredDisplays.length === 0"
                  class="theme-dropdown-empty"
                >
                  No Social Graphics found
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label
            for="postAnimation"
            class="sidebar-form-label"
          >
            Post Animation
          </label>
        </div>
        <div class="settings-col-12">
          <select
            id="postAnimation"
            v-model="postAnimation"
            class="styled-select"
            data-test="graphics-socialDisplaySettings-postAnimation"
            data-entity="graphics-socialDisplaySettings-postAnimation"
          >
            <option
              v-for="animation in availableAnimations"
              :key="animation.value"
              :value="animation.value"
            >
              {{ animation.label }}
            </option>
          </select>
        </div>
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Preview Social Data"
      collapsible
      :element-type="element.type"
      data-test="graphics-socialDisplaySettings-preview"
    >
      <div class="settings-row sidebar-form-group">
        <div class="settings-col-12">
          <label
            for="feedSelected"
            class="sidebar-form-label"
          >Select a Board</label>
        </div>
        <div class="settings-col-12">
          <select
            id="feedSelected"
            v-model="feedSelected"
            class="styled-select"
            data-test="graphics-selectTagboard"
            data-entity="graphics-selectTagboard"
          >
            <option
              v-for="tagboard in tagboards"
              :key="tagboard._id"
              :value="tagboard._id"
            >
              {{ tagboard.name }}
            </option>
          </select>
        </div>
      </div>
    </SidebarSettingsGroup>

    <SidebarSettingsGroup
      title="Style & Animation"
      collapsible
      :element-type="element.type"
      data-test="graphics-socialDisplaySettings-styleAndAnimation"
    >
      <template>
        <opacity-settings v-model="opacity" />
        <hr class="sidebar-divider" />
      </template>

      <div class="sidebar-form-group">
        <border-radius
          v-model:top-left="borderTopLeftRadius"
          v-model:top-right="borderTopRightRadius"
          v-model:bottom-left="borderBottomLeftRadius"
          v-model:bottom-right="borderBottomRightRadius"
        />
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="sidebar-form-label">Background color</label>
        <label
          class="radio inline-radio"
          for="solid"
        >
          Solid
          <input
            id="solid"
            v-model="fillStyle"
            type="radio"
            value="solid"
            data-test="graphics-socialDisplaySettings-fillStyleSolid"
            data-entity="graphics-socialDisplaySettings-fillStyleSolid"
          >
          <div class="checkmark" />
        </label>

        <label
          class="radio inline-radio"
          for="gradient"
        >
          Gradient
          <input
            id="gradient"
            v-model="fillStyle"
            type="radio"
            value="gradient"
            data-test="graphics-socialDisplaySettings-fillStyleGradient"
            data-entity="graphics-socialDisplaySettings-fillStyleGradient"
          >
          <div class="checkmark" />
        </label>

        <div
          v-if="fillStyle == 'solid'"
          class="colorpicker text-center"
          data-test="graphics-socialDisplaySettings-fillStyleSolidColor"
        >
          <colorpicker
            v-model="backgroundColor"
          />
        </div>

        <div
          v-else
          class="gradient-color-picker"
        >
          <div class="colorpicker text-center">
            <colorpicker
              v-model="gradientColorOne"
            />
          </div>
          <div class="colorpicker text-center">
            <colorpicker
              v-model="gradientColorTwo"
            />
          </div>
        </div>
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <box-shadow-settings :element="element" />
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Border
          <input
            v-model="border"
            type="checkbox"
            data-test="graphics-socialDisplaySettings-toggleBorder"
            data-entity="graphics-socialDisplaySettings-toggleBorder"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <template v-if="border">
          <div class="settings-row settings-row-xs">
            <div class="settings-col-6 settings-col-xs">
              <input
                v-model.number="borderWidth"
                type="number"
                name="borderWidth"
                min="0"
                data-test="graphics-socialDisplaySettings-setBorderWidth"
                data-entity="graphics-socialDisplaySettings-setBorderWidth"
              >
              <label class="sidebar-input-sub-label">Width</label>
            </div>
            <div class="settings-col-6 settings-col-xs colorpicker">
              <colorpicker
                v-model="borderColor"
              />
              <label class="sidebar-input-sub-label">Color</label>
            </div>
          </div>
        </template>
      </div>

      <hr class="sidebar-divider" />

      <div class="sidebar-form-group">
        <label class="checkbox header-checkbox">Blur
          <input
            v-model="blur"
            type="checkbox"
            data-test="graphics-socialDisplaySettings-toggleBlur"
            data-entity="graphics-socialDisplaySettings-toggleBlur"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check" />
          </div>
        </label>

        <template v-if="blur">
          <div class="settings-row">
            <div class="settings-col-12">
              <input
                v-model.number="blurSize"
                type="number"
                min="0"
                data-test="graphics-socialDisplaySettings-setBlurSize"
                data-entity="graphics-socialDisplaySettings-setBlurSize"
              >
              <label class="sidebar-input-sub-label">Blur Size</label>
            </div>
          </div>
        </template>
      </div>

      <hr class="sidebar-divider" />

      <animation-settings
        v-if="!multipleSelected"
        :element="element"
      />
    </SidebarSettingsGroup>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import Themes from '../../../../services/themes';

import SettingsMixins from '../../../../mixins/settings-mixins';
import BorderRadius from '../../BorderRadius.vue';
import BoxShadowSettings from '../../BoxShadow.vue';
import OpacitySettings from '../../Opacity.vue';
import AnimationSettings from '../../Animation.vue';

export default {
  name: 'SocialDisplaySettings',

  components: {
    BorderRadius,
    BoxShadowSettings,
    OpacitySettings,
    AnimationSettings,
  },

  mixins: [SettingsMixins],

  props: {
    element: {
      type: Object,
      required: true,
    },
    template: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      displays: [],
      tagboards: [],
      showThemeDropdown: false,
      themeSearchQuery: '',
      loadingThemes: false,
      themesPage: 1,
      themesLimit: 20,
      themesTotal: 0,
      allDisplaysLoaded: false,
      animationsByLayout: {
        isolated: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Bounce', label: 'Bounce' },
          { value: 'Roll', label: 'Roll' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Fade', label: 'Fade' },
          { value: 'Zoom', label: 'Zoom' },
          { value: 'Swing', label: 'Swing' },
          { value: 'Fly', label: 'Fly' },
        ],
        cinematic: [
          { value: 'Fade', label: 'Fade' },
          { value: 'Ken Burns', label: 'Ken Burns' },
          { value: 'Zoom In', label: 'Zoom In' },
          { value: 'Zoom Out', label: 'Zoom Out' },
        ],
        lowerthird: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Bounce', label: 'Bounce' },
          { value: 'Roll', label: 'Roll' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Fade', label: 'Fade' },
          { value: 'Zoom', label: 'Zoom' },
          { value: 'Swing', label: 'Swing' },
          { value: 'Fly', label: 'Fly' },
        ],
        lowerthirdscroll: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Bounce', label: 'Bounce' },
          { value: 'Roll', label: 'Roll' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Fade', label: 'Fade' },
          { value: 'Zoom', label: 'Zoom' },
          { value: 'Swing', label: 'Swing' },
          { value: 'Fly', label: 'Fly' },
        ],
        ticker: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Fade', label: 'Fade' },
        ],
        upnext: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Slide Down', label: 'Slide Down' },
          { value: 'Fade', label: 'Fade' },
        ],
        waterfall: [
          { value: 'Fade', label: 'Fade' },
          { value: 'Slide', label: 'Slide' },
          { value: 'Slide Down', label: 'Slide Down' },
          { value: 'Flip', label: 'Flip' },
          { value: 'Zoom', label: 'Zoom' },
          { value: 'Scale', label: 'Scale' },
          { value: 'Parallax', label: 'Parallax' },
        ],
        grid: [
          { value: 'Fade', label: 'Fade' },
          { value: 'Slide', label: 'Slide' },
          { value: 'Slide Down', label: 'Slide Down' },
          { value: 'Flip', label: 'Flip' },
        ],
        carousel: [
          { value: 'Slide', label: 'Slide' },
          { value: 'Scale', label: 'Scale' },
          { value: 'Flow', label: '3D' },
        ],
        mural: [
          { value: 'Fade', label: 'Fade' },
        ],
        chat: [
          { value: 'Slide', label: 'Slide' },
        ],
        stories: [
          { value: 'Fade', label: 'Fade' },
        ],
      },
    };
  },

  computed: {
    multipleSelected() {
      return this.$store.state.templates.selectedElements.length > 1;
    },

    selectedDisplayTitle() {
      const display = this.displays.find((d) => d._id === this.displayId);
      return display ? display.title : '';
    },

    filteredDisplays() {
      if (!this.themeSearchQuery) {
        return this.displays;
      }

      const query = this.themeSearchQuery.toLowerCase();
      return this.displays.filter((display) => display.title.toLowerCase().includes(query));
    },

    currentDisplay() {
      return this.displays.find((d) => d._id === this.displayId);
    },

    currentLayout() {
      return this.currentDisplay?.layout;
    },

    availableAnimations() {
      const layout = this.currentLayout;
      return this.animationsByLayout[layout] || [{ value: 'Fade', label: 'Fade' }];
    },

    feedSelected: {
      get() {
        return this.getPropertyValue('previewTagboardId', { emptyValue: '396837' });
      },
      set(v) {
        this.updateOneElementValue('previewTagboardId', v);
      },
    },

    displayId: {
      get() {
        return this.getPropertyValue('displayId');
      },

      set(displayId) {
        const prevDisplayId = this.element.displayId;

        // If user did not override layer name, then set name to new display
        const prevDisplay = prevDisplayId && this.displays.find((d) => d._id === prevDisplayId);
        if (prevDisplay?.title === this.element.name) {
          const newDisplay = this.displays.find((d) => d._id === displayId);
          this.updateOneElementValue('name', newDisplay.title);
        }

        this.updateOneElementValue('displayId', displayId);
      },
    },

    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },

    borderTopLeftRadius: {
      get() {
        return this.getPropertyValue('borderTopLeftRadius');
      },
      set(v) {
        this.updateOneElementValue('borderTopLeftRadius', v);
      },
    },

    borderTopRightRadius: {
      get() {
        return this.getPropertyValue('borderTopRightRadius');
      },
      set(v) {
        this.updateOneElementValue('borderTopRightRadius', v);
      },
    },

    borderBottomLeftRadius: {
      get() {
        return this.getPropertyValue('borderBottomLeftRadius');
      },
      set(v) {
        this.updateOneElementValue('borderBottomLeftRadius', v);
      },
    },

    borderBottomRightRadius: {
      get() {
        return this.getPropertyValue('borderBottomRightRadius');
      },
      set(v) {
        this.updateOneElementValue('borderBottomRightRadius', v);
      },
    },

    fillStyle: {
      get() {
        return this.getPropertyValue('fillStyle', { emptyValue: 'solid' });
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },

    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor', { emptyValue: 'transparent' });
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },

    gradientColorOne: {
      get() {
        return this.getPropertyValue('gradientColorOne', { emptyValue: '#00c4dd' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },

    gradientColorTwo: {
      get() {
        return this.getPropertyValue('gradientColorTwo', { emptyValue: '#00bad7' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },

    boxShadow: {
      get() {
        return this.getPropertyValue('boxShadow');
      },
      set(v) {
        this.updateOneElementValue('boxShadow', v);
      },
    },

    boxShadowX: {
      get() {
        return this.getPropertyValue('boxShadowX', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('boxShadowX', v);
      },
    },

    boxShadowY: {
      get() {
        return this.getPropertyValue('boxShadowY', { emptyValue: 2 });
      },
      set(v) {
        this.updateOneElementValue('boxShadowY', v);
      },
    },

    boxShadowBlur: {
      get() {
        return this.getPropertyValue('boxShadowBlur', { emptyValue: 4 });
      },
      set(v) {
        this.updateOneElementValue('boxShadowBlur', v);
      },
    },

    boxShadowSpread: {
      get() {
        return this.getPropertyValue('boxShadowSpread', { emptyValue: 0 });
      },
      set(v) {
        this.updateOneElementValue('boxShadowSpread', v);
      },
    },

    boxShadowColor: {
      get() {
        return this.getPropertyValue('boxShadowColor', { emptyValue: '#000000' });
      },
      set(v) {
        this.updateOneElementValue('boxShadowColor', v);
      },
    },

    border: {
      get() {
        return this.getPropertyValue('border');
      },
      set(v) {
        this.updateOneElementValue('border', v);
      },
    },

    borderWidth: {
      get() {
        return this.getPropertyValue('borderWidth', { emptyValue: 2 });
      },
      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },

    borderColor: {
      get() {
        return this.getPropertyValue('borderColor', { emptyValue: '#000000' });
      },
      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },

    blur: {
      get() {
        return this.getPropertyValue('blur');
      },
      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },

    blurSize: {
      get() {
        return this.getPropertyValue('blurSize', { emptyValue: 10 });
      },
      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },

    postAnimation: {
      get() {
        const value = this.getPropertyValue('postAnimation');
        if (value !== undefined && value !== null) {
          return value;
        }

        // Get default from theme settings
        const themeAnimation = this.currentDisplay?.settings?.animation;
        if (themeAnimation) {
          return themeAnimation;
        }

        // Fallback to first available animation for the layout
        return this.availableAnimations[0]?.value ?? 'Fade';
      },
      set(v) {
        this.updateOneElementValue('postAnimation', v);
      },
    },
  },

  watch: {
    currentDisplay: {
      immediate: true,
      handler(newDisplay, oldDisplay) {
        // Initialize defaults from theme when display is first loaded or changed
        if (newDisplay && newDisplay !== oldDisplay) {
          this.initializeDefaultsFromTheme(newDisplay, oldDisplay);
        }
      },
    },
  },

  async mounted() {
    await this.loadDisplays();
    await this.loadTagboards();
    document.addEventListener('click', this.handleClickOutside);

    // Initialize defaults after displays are loaded
    if (this.currentDisplay) {
      this.initializeDefaultsFromTheme(this.currentDisplay, null);
    }
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  methods: {
    async loadDisplays(search = '', page = 1) {
      try {
        this.loadingThemes = true;
        const query = {
          page,
          limit: this.themesLimit,
        };

        if (search) {
          query.search = search;
        }

        const response = await Themes.getThemes(query);

        if (page === 1) {
          this.displays = response.themes || [];
        } else {
          this.displays = [...this.displays, ...(response.themes || [])];
        }

        this.themesTotal = response.total || 0;
        this.themesPage = page;
        this.allDisplaysLoaded = this.displays.length >= this.themesTotal;
      } catch (error) {
        console.error('Error loading displays:', error);
      } finally {
        this.loadingThemes = false;
      }
    },

    debouncedLoadDisplays: debounce(function (search) {
      this.loadDisplays(search, 1);
    }, 300),

    onThemeSearchInput() {
      this.themesPage = 1;
      this.allDisplaysLoaded = false;
      this.debouncedLoadDisplays(this.themeSearchQuery);
    },

    onThemeScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (scrolledToBottom && !this.loadingThemes && !this.allDisplaysLoaded) {
        this.loadDisplays(this.themeSearchQuery, this.themesPage + 1);
      }
    },

    toggleThemeDropdown() {
      this.showThemeDropdown = !this.showThemeDropdown;
      if (this.showThemeDropdown) {
        this.$nextTick(() => {
          if (this.$refs.themeSearchInput) {
            this.$refs.themeSearchInput.focus();
          }
        });
      }
    },

    selectDisplay(display) {
      this.displayId = display._id;
      this.updateOneElementValue('layout', display.layout);
      this.showThemeDropdown = false;
    },

    handleClickOutside(event) {
      const dropdown = this.$el.querySelector('.theme-dropdown-wrapper');
      if (dropdown && !dropdown.contains(event.target)) {
        this.showThemeDropdown = false;
      }
    },

    async loadTagboards() {
      try {
        await this.$store.dispatch('getUserTagboards');
        this.tagboards = this.$store.state.user.tagboards || [];
      } catch (error) {
        console.error('Error loading tagboards:', error);
      }
    },

    getLayoutLabel(layout) {
      if (!layout) return 'Social Graphic';

      const labels = {
        grid: 'Grid Layout',
        waterfall: 'Waterfall Layout',
        carousel: 'Carousel Layout',
        isolated: 'Isolated Layout',
        scrolling: 'Scrolling Layout',
        lowerthird: 'Lower Third Layout',
        lowerthirdscroll: 'Lower Third Layout',
        cinematic: 'Cinematic Layout',
        ticker: 'Ticker Layout',
      };

      return labels[layout] || layout.charAt(0).toUpperCase() + layout.slice(1);
    },

    initializeDefaultsFromTheme(newDisplay, oldDisplay) {
      if (!newDisplay?.settings) {
        return;
      }

      const isThemeChange = !!oldDisplay && oldDisplay._id !== newDisplay._id;

      // Post Animation - handle validation and defaults
      const currentAnimation = this.getPropertyValue('postAnimation');

      if (currentAnimation === undefined) {
        // No animation set - use theme default
        if (newDisplay.settings.animation) {
          this.postAnimation = newDisplay.settings.animation;
        }
      } else if (isThemeChange) {
        // Theme changed - check if current animation is valid for new layout
        const newLayoutAnimations = this.animationsByLayout[newDisplay.layout] || [];
        const isValidForNewLayout = newLayoutAnimations
          .some((anim) => anim.value === currentAnimation);

        if (!isValidForNewLayout) {
          // Current animation is not valid for new layout - use theme default or first available
          const newAnimation = newDisplay.settings.animation || newLayoutAnimations[0]?.value || 'Fade';
          this.postAnimation = newAnimation;
        } else {
          // Animation is valid, so check if it matches old theme default and update to new default
          const matchesOldDefault = oldDisplay?.settings?.animation === currentAnimation;
          if (matchesOldDefault && newDisplay.settings.animation) {
            this.postAnimation = newDisplay.settings.animation;
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gradient-color-picker {
  display: flex;
  gap: 0.5rem;
}

.theme-dropdown-wrapper {
  position: relative;
  width: 100%;
}

.theme-dropdown-trigger {
  width: 100%;
  display: block;
  padding: 3px;
  padding-right: 24px;
  color: #333;
  line-height: 1.35;
  border-radius: 4px;
  background-color: #f9f9f9;
  border: 1px solid #ccc;
  box-shadow: none;
  background-image: url(/img/triangle-down.svg);
  background-size: 8px auto;
  background-position: calc(100% - 8px) 50%;
  background-repeat: no-repeat;
  cursor: pointer;

  &:hover {
    border-color: #999;
  }

  &:focus,
  &:focus-within {
    border-color: #00B4CC;
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 8px rgba(0, 180, 204, .6);
    outline: 0;
  }
}

.theme-dropdown-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.theme-dropdown-arrow {
  display: none;
}

.theme-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 320px;
  display: flex;
  flex-direction: column;
}

.theme-search-wrapper {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

.theme-search-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #00bad7;
  }
}

.theme-dropdown-list {
  overflow-y: auto;
  max-height: 260px;
}

.theme-dropdown-item {
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }

  &.active {
    background-color: #ECFCFF;
    color: #00bad7;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
  }

  &__subtitle {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
    line-height: 1.3;
  }

  &.active &__subtitle {
    color: #00bad7;
    opacity: 0.8;
  }
}

.theme-dropdown-loading,
.theme-dropdown-empty {
  padding: 12px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.styled-select {
  width: 100%;
  padding: 3px;
  padding-right: 24px;
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.35;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8"><path fill="%23666" d="M6 8L0 0h12z"/></svg>');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 8px;

  &:hover {
    border-color: #999;
  }

  &:focus {
    outline: none;
    border-color: #00bad7;
  }
}
</style>
